"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { RecoveryRequest } from "@shared/proto/tenki/cloud/identity/v1beta1/identity_service_pb";
import { recoveryRequestSchema } from "@shared/proto/tenki/cloud/identity/v1beta1/identity_service_zod";
import { Button } from "@shared/ui/button";
import { ButtonLabelLoader } from "@shared/ui/components/button-label-loader";
import { Input } from "@shared/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared/ui/old-form";
import { toast } from "@shared/ui/toast";

import { api } from "~/trpc/client";
import { FROM_RECOVERY_KEY } from "~/utils/onboarding/constants";

const FlowState = ["choose_method", "sent_email", "passed_challenge"] as const;
export type FlowState = (typeof FlowState)[number];

function RecoveryForm({ flowId, flowState, csrfToken }: { flowId: string; flowState: FlowState; csrfToken: string }) {
  const router = useRouter();
  const [recoveryState, setRecoveryState] = useState<FlowState>(flowState);

  useEffect(() => {
    if (flowState === "passed_challenge") {
      router.push("/settings");
    }
  }, [flowState, router]);

  const message =
    recoveryState === "choose_method" ? "Enter your email to recover your account" : "Enter code sent to your email";

  const form = useForm<RecoveryRequest>({
    resolver: zodResolver(
      recoveryRequestSchema.extend({
        email:
          recoveryState === "choose_method"
            ? recoveryRequestSchema.shape.email.unwrap().min(1, "Email is required").email("Must be a valid email")
            : recoveryRequestSchema.shape.email,
        code:
          recoveryState === "sent_email"
            ? recoveryRequestSchema.shape.code.unwrap().min(6, "Code is required")
            : recoveryRequestSchema.shape.email,
      }),
    ),
    defaultValues: {
      flowId,
      csrfToken,
      email: "",
      code: "",
    },
    mode: "onChange",
  });

  const { mutate: recover, isPending } = api.identity.public.recovery.useMutation({
    onError: (error) => toast.error(error.message),
    onSuccess: (res) => {
      if (recoveryState === "choose_method") {
        toast.success("Recovery code sent to your email");
        setRecoveryState("sent_email");
      } else if (recoveryState === "sent_email") {
        if (res.status === "ok") {
          sessionStorage.setItem(FROM_RECOVERY_KEY, "true");
          toast.success(res.message);
          window.location.href = res.returnTo;
        } else {
          toast.error(res.message);
        }
      }
    },
    trpc: { context: { skipBatch: true } },
  });

  const onSubmit = (values: RecoveryRequest) => {
    if (recoveryState === "choose_method") {
      if (values.email) {
        recover({ ...values, csrfToken, code: undefined });
      }
    } else if (recoveryState === "sent_email") {
      if (values.code) {
        recover({ ...values, csrfToken, email: undefined });
      }
    }
  };

  return (
    <>
      <section className="mb-8">
        <h1 className="text-static-primary mb-2 text-lg font-bold">Recovery</h1>
        <p className="text-static-secondary">{message}</p>
      </section>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4" noValidate>
          {recoveryState === "choose_method" && (
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        id="email"
                        type="email"
                        placeholder="me@example.com"
                        {...field}
                        className={form.formState.errors.email ? "border-input-controls-error" : ""}
                      />
                    </FormControl>
                    {form.formState.touchedFields.email && <FormMessage />}
                  </FormItem>
                )}
              />
            </div>
          )}

          {recoveryState === "sent_email" && (
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input
                        id="code"
                        type="password"
                        maxLength={6}
                        {...field}
                        className={form.formState.errors.code ? "border-input-controls-error" : ""}
                      />
                    </FormControl>
                    {form.formState.touchedFields.code && <FormMessage />}
                  </FormItem>
                )}
              />
            </div>
          )}

          <Button type="submit" className="w-full" disabled={!form.formState.isValid || isPending}>
            <ButtonLabelLoader isPending={isPending} label="Recover" />
          </Button>
        </form>
      </Form>
      <Link
        href="/auth/login"
        prefetch={false}
        className="text-link-rest mt-6 inline-block w-full text-center text-xs underline underline-offset-[3px]"
      >
        Go back to Sign In Page
      </Link>
    </>
  );
}

export default RecoveryForm;
